import React from 'react';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Briefcase, 
  Hammer, 
  Wrench, 
  BarChart3, 
  Settings, 
  LogOut,
  Sparkles
} from 'lucide-react';
import { PageView, SubscriptionPlan } from '../types';

interface SidebarProps {
  currentPage: PageView;
  setPage: (page: PageView) => void;
  lang: 'en' | 'pt';
  subscription?: UserSubscription;
  onUpgrade: () => void;
}

interface UserSubscription {
  plan: SubscriptionPlan;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPage, setPage, lang, subscription, onUpgrade }) => {
  
  const menuItems = [
    { id: 'dashboard', label: lang === 'pt' ? 'Visão Geral' : 'Dashboard', icon: LayoutDashboard },
    { type: 'divider', label: lang === 'pt' ? 'Ativos' : 'Assets' },
    { id: 'properties', label: lang === 'pt' ? 'Imóveis' : 'Properties', icon: Building2 },
    { id: 'tenants', label: lang === 'pt' ? 'Inquilinos' : 'Tenants', icon: Users },
    { type: 'divider', label: lang === 'pt' ? 'Crescimento' : 'Growth' },
    { id: 'deals', label: 'Deal Flow', icon: Briefcase },
    { id: 'renovations', label: lang === 'pt' ? 'Obras' : 'Renovations', icon: Hammer },
    { type: 'divider', label: lang === 'pt' ? 'Gestão' : 'Management' },
    { id: 'maintenance', label: lang === 'pt' ? 'Manutenção' : 'Maintenance', icon: Wrench },
    { id: 'analytics', label: lang === 'pt' ? 'Finanças' : 'Finance', icon: BarChart3 },
  ];

  return (
    <aside className="w-64 bg-slate-950 border-r border-slate-800 flex flex-col h-full shrink-0 transition-all duration-300">
      {/* Logo Area */}
      <div className="h-24 flex items-center px-8 border-b border-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Building2 className="text-white w-5 h-5" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg tracking-tight leading-none">DC PropChain</h1>
            <span className="text-[10px] text-slate-500 font-medium tracking-widest uppercase">Invest OS</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
        {menuItems.map((item, index) => {
          if (item.type === 'divider') {
            return (
              <div key={`div-${index}`} className="px-4 pt-6 pb-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{item.label}</p>
              </div>
            );
          }

          const Icon = item.icon as any;
          const isActive = currentPage === item.id;

          return (
            <button
              key={item.id}
              onClick={() => setPage(item.id as PageView)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group ${
                isActive 
                  ? 'bg-emerald-500/10 text-emerald-400 shadow-sm border border-emerald-500/20' 
                  : 'text-slate-400 hover:text-slate-100 hover:bg-slate-900'
              }`}
            >
              <Icon className={`w-5 h-5 transition-colors ${isActive ? 'text-emerald-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Footer / Upgrade */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/30">
        {subscription?.plan === 'Starter' && (
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 mb-4 border border-slate-700 relative overflow-hidden group cursor-pointer" onClick={onUpgrade}>
            <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
              <Sparkles className="w-12 h-12 text-emerald-400" />
            </div>
            <h4 className="text-white text-sm font-bold mb-1">Upgrade to Pro</h4>
            <p className="text-xs text-slate-400 mb-3">Unlock AI analysis & unlimited properties.</p>
            <button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold py-2 rounded-lg transition-colors">
              Upgrade Now
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;